import {view, Icon, Tooltip} from '@sail/ui';
import {info} from '@sail/icons/react/Icon';
import {FormTextField} from 'src/v2/components/anyCrypto/FormTextField';
import {getNetworkName} from 'src/components/Wallet/constants';
import type {OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum} from '@stripe-internal/data-v1/schema/types';
import type {FormatXMLElementFn} from '@sail/intl';
import {message, useIntl} from '@sail/intl';

const messages = {
  refundAddressLabel: message({
    id: 'crypto.payins.v2.refund.refund_address_label',
    description: 'Label for refund wallet address input',
    defaultMessage: 'Refund wallet address',
  }),

  refundAddressTooltip: message<{b: FormatXMLElementFn}>({
    id: 'crypto.payins.v2.refund.refund_address_tooltip',
    description:
      'Tooltip explaining why a customer may need to set a refund address',
    defaultMessage:
      "In certain cases, your deposit may be refunded. If you're paying from an <b>exchange</b> or <b>a wallet you don't control</b>, set a refund address you own.",
  }),

  refundAddressPlaceholder: message({
    id: 'crypto.payins.v2.refund.refund_address_placeholder',
    description: 'Placeholder for refund wallet address input',
    defaultMessage: 'Enter your refund address',
  }),

  refundNetworkLabel: message<{
    networkName: React.ReactNode;
  }>({
    id: 'crypto.payins.v2.refund.refund_network_label',
    description: 'Label showing which network the refund address is for',
    defaultMessage: 'Refund network: {networkName}',
  }),
};

type RefundAddressFieldProps = {
  value: string;
  onChange: (value: string) => void;
  selectedNetwork: OpenapiCryptocurrencyPayinsResourceSupportedCurrencyCurrencyNetworkEnum;
  error?: string;
  showTooltip?: boolean;
};

export const RefundAddressField = ({
  value,
  onChange,
  selectedNetwork,
  error,
  showTooltip = false,
}: RefundAddressFieldProps) => {
  const intl = useIntl();

  const addressLabel = intl.formatMessage(messages.refundAddressLabel);

  const label = showTooltip ? (
    <view.div css={{stack: 'x', gap: 'xsmall', alignItems: 'center'}}>
      {addressLabel}
      <Tooltip trigger={<Icon icon={info} size="xsmall" />}>
        {intl.formatMessage(messages.refundAddressTooltip, {
          b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
        })}
      </Tooltip>
    </view.div>
  ) : (
    addressLabel
  );

  return (
    <FormTextField
      label={label}
      description={intl.formatMessage(messages.refundNetworkLabel, {
        networkName: getNetworkName(selectedNetwork),
      })}
      placeholder={intl.formatMessage(messages.refundAddressPlaceholder)}
      value={value}
      onChange={onChange}
      error={error}
    />
  );
};
